import {Injectable} from "@angular/core";
import {TranslocoService} from "@ngneat/transloco";
import {ToastrService} from "ngx-toastr";
import {MatDialog} from "@angular/material/dialog";
import {Compbaser} from "../components/compbaser";
import {StoreSelectorsService} from "./store-selectors.service";
import {IBackground, ICRUDRecord, Id, ILayoutConstraints, IPage, ISceneBlock, IPlayerProps} from "../interfaces";
import {DialogPopComponent} from "../components/dialog-pop/dialog-pop.component";
// type-only: the rich text editor dialog is loaded on demand (see openRichTextEditor)
import type {RichTextEditorDialogData} from "../components/dialog-rich-text-editor/dialog-rich-text-editor.component";
import {actionAddSceneItem, actionCRUDRecord, actionRemoveInSceneItem, actionRemoveSceneBlocks, actionRemoveScenes, actionSceneItemPlaylist, actionSelectedSceneItemId, actionSelectedSchedule, actionUpdateSceneItem, actionUpdateSceneItemNoHistory, actionSceneBreadcrumbsPush, actionSceneBreadcrumbsSet, actionSelectedSceneId, actionSelectedSceneTouchId, actionSceneJumpLinked} from "../store/actions/user.actions";
import {LocalStoreService} from "./local-store.service";
import {ContainerService} from "./container.service";
import {AppBusyService} from "./app-busy.service";
import {IBlockLabelProp, MimeAnomalyType} from "../containers/editor/scene-blocks/block-constants";
import {requiresToolboxContent} from "../containers/editor/scene-blocks/toolbox-content";
import {findSceneItemDeep, getGroupChildren, getSceneItemPathCrumbs, getTotalLengthPlaylist, isDigit, isGroupItem, isLabelItem, isMainTouchScene, isNullOrUndefOrEmpty, isResourceItem, isSubScene, MAX_PLAYLIST_TIME, sanitizeResourceLabel} from "../common";
import {TouchLib} from "../containers/editor/touch/touchLib";
import {flashTextToHtml, htmlToFlashText, extractLegacyText, legacyTextAttrs} from "../common/legacy-rich-text";
// type-only: the asset picker dialog is loaded on demand (see openGetAssetDialog)
import type {IDialogGetAsset} from "../components/dialog-get-asset/dialog-get-asset.component";
import {SceneMimeTypes} from "../containers/editor/scene-blocks/mime-constants";
import {take} from "rxjs/operators";
import {IDrillPath} from "../store/reducers/undoable-actions";
import {Lib} from "../../libs/Lib";
import {Router} from "@angular/router";
import * as _ from 'lodash';

@Injectable()
export class SceneService extends Compbaser {

    /** true while a lazily loaded dialog chunk is in flight, so a double click cannot open two */
    private m_assetDialogOpening = false;
    private m_richTextDialogOpening = false;
    sceneId: Id;
    players: ISceneBlock[] = [];
    studioId: string;
    sceneLayout: ILayoutConstraints;
    breadcrumbs: Array<any> = [];

    constructor(
        private storeSelector: StoreSelectorsService,
        private translocoService: TranslocoService,
        private router: Router,
        private localStoreService: LocalStoreService,
        private container: ContainerService,
        private toastr: ToastrService,
        private busyService: AppBusyService,
        protected override dialog: MatDialog
    ) {
        super(dialog);
        this.cod(
            this.storeSelector.listenSelectedSceneId().subscribe((_sceneId: Id) => {
                this.sceneId = _sceneId;
            })
        );
        this.cod(
            this.storeSelector.listenSelectedScene().subscribe((i_scene: IPlayerProps) => {
                if (i_scene && i_scene.Player) {
                    this.sceneLayout = i_scene.Player.Data[0].Layout[0].$;
                    this.players = (i_scene.Player.Data["0"]?.Scene["0"]?.Players["0"]?.Player || []) as ISceneBlock[];
                }
            })
        );
        this.cod(
            this.storeSelector.getSceneBreadcrumbs().subscribe((breadcrumbs) => {
                this.breadcrumbs = breadcrumbs || [];
            })
        );
        this.studioId = this.localStoreService.getStorageValConfig('studioId');
    }


    /**
     * remove scene items by provided ids
     */
    removeSceneItems(ids: Id[], checkPrevateChild: boolean = true) {
        if (!ids || ids.length == 0) return;

        // drilled INTO a Group: the ids are the group's children, so the removal must target the group's own
        // inner Scene, not the scene's top-level Players (which never holds them). null -> the scene itself.
        // sceneContainerId is what the canvas is actually editing (see ContainerService) — the one source of
        // truth for where a structural write lands.
        const containerId = this.container.sceneContainerId.value;
        const parentItemId = (containerId && `${containerId}` !== `${this.sceneId}`) ? containerId : null;

        // a drilled-into container is always a Group (keeps its children under <Group>); the scene keeps them
        // under <Scene>.
        const childKey = parentItemId ? 'Group' : 'Scene';
        const values = ids.map((_id) => {
            return {
                key: null,
                value: _id,
                storePath: `Data[0].${childKey}[0].Players[0].Player`
            };
        });

        if (checkPrevateChild) {
            // check private child
            const privateChildIds = [];
            ids.forEach((_id) => {
                const player = this.players.find((_player) => (_player.$.id == _id) && (`${_player.$.private_child}` === 'true'));
                if (player) {
                    privateChildIds.push(player.$.src);
                }
            });

            // if deleted item is private child scene, remove it from scene list permanently
            if (privateChildIds.length > 0) {
                this.storeSelector.dispatch(actionRemoveScenes({
                    sceneIds: privateChildIds
                }));

                const saveRecord: ICRUDRecord = {
                    studioId: this.studioId,
                    command: 'delete',
                    primary: 'player_data_id',
                    table: 'player_data',
                    body: null,
                    id: privateChildIds
                };
                this.storeSelector.dispatch(actionCRUDRecord({saveRecord}));
            }
        }

        // dispatch remove action and select scene
        this.storeSelector.dispatch(actionRemoveSceneBlocks({
            sceneId: this.sceneId,
            sceneItemId: parentItemId,
            values
        }));
    }

    /**
     *
     * @param color : decimal (0 ~ 2^24)
     * @param clearImage
     */
    setSceneBackgroundColor(color: 'transparent' | number, clearImage: boolean = true) {
        if (clearImage) {
            this.clearBackgroundImage();
        }
        if (color === 'transparent') {
            this.storeSelector.dispatch(actionRemoveInSceneItem({
                sceneId: this.sceneId,
                sceneItemId: this.sceneId,
                values: [{
                    key: 'Background',
                    value: null,
                    storePath: 'Data[0]'
                }]
            }));
        } else if (color) {
            const effectBackground: IBackground = {color: `${color}`, alpha: '1', style: ''};
            this.storeSelector.dispatch(actionUpdateSceneItem({
                sceneId: this.sceneId,
                sceneItemId: this.sceneId,
                values: [{
                    key: 'Background',
                    value: [{
                        $: effectBackground
                    }],
                    storePath: 'Data[0]',
                }]
            }));
        }
    }

    setBackgroundImage(bgInfo) {
        const {resId, resType, showConfirmDialog} = bgInfo;
        const blockAdding = this.storeSelector.getBlockLabelByFileType(resType);
        blockAdding.template.Data["0"].Resource["0"].$.resource = resId;

        const bgId = this.getBackgroundImageId();
        if (bgId) {
            // if there already exists background image
            if (showConfirmDialog) {
                const msg = this.translocoService.translate('message.changeBackgroundv2', {}, this.translocoService.getActiveLang());
                const dialogRef = this.dialog.open(DialogPopComponent, {
                    height: '333px',
                    width: '400px',
                    data: {name: msg, showCancel: true}
                });
                dialogRef.afterClosed().subscribe((r) => {
                    if (r) {
                        this.removeSceneItems([bgId], false);
                        this.addItemAsBackground(blockAdding.template);
                    }
                });
            } else {
                this.removeSceneItems([bgId], false);
                this.addItemAsBackground(blockAdding.template);
            }

        } else {
            this.addItemAsBackground(blockAdding.template);
        }
    }

    addItemAsBackground(addSceneItem) {
        if (!this.sceneLayout) return;
        const newLayout = addSceneItem.Data[0].Layout[0].$;
        addSceneItem.$.id = _.uniqueId('__');

        newLayout.x = '0';
        newLayout.y = '0';
        newLayout.width = this.sceneLayout.width;
        newLayout.height = this.sceneLayout.height;
        newLayout.locked = 'true';
        newLayout.isBackground = 'true';

        this.storeSelector.dispatch(actionAddSceneItem({
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            updatedItemIds: [addSceneItem.$.id],
            values: [{
                key: 'Player',
                cloneValue: false,
                value: addSceneItem,
                storePath: 'Data["0"].Scene["0"].Players[0]',
                addIndex: 0
            }],
        }));

        /* Alon: 'reorder' action is now happening in the Lib immediately after the actionAddSceneItem
        setTimeout(() => {
            this.storeSelector.dispatch(actionCRUDScene({
                data: {
                    command: 'reorder',
                    sceneId: this.sceneId,
                    itemIds: []
                }
            }));
            this.storeSelector.dispatch(actionCRUDScene({
                data: {
                    command: 'update',
                    sceneId: this.sceneId,
                    itemIds: [addSceneItem.$.id]
                }
            }));
        });

         */
    }


    /**
     * Check if current first bottom item is background or not
     * @returns player id
     */
    getBackgroundImageId() {
        if (this.players && this.players.length > 0) {
            // no Layout -> a playlist-page player (fills its slot): it can never be the scene background
            const layout = this.players["0"]?.Data?.["0"]?.Layout?.["0"]?.$;
            if (layout && String(layout.isBackground) === 'true' && String(layout.locked) === 'true') {
                return this.players["0"].$.id;
            }
        }
        return '';
    }

    /**
     * Clear background image if exists
     */
    clearBackgroundImage() {
        const bgId = this.getBackgroundImageId();
        if (bgId) {
            this.removeSceneItems([bgId], false);
        }
    }

    /**
     * Update the layout of scene item.
     * If isBgImage is true, update the layout of background image
     */
    updateSceneItemLayout(sceneItemId: Id, newLayout: any, isBgImage: boolean = false) {
        if (!newLayout) return;
        if (isBgImage) {
            sceneItemId = this.getBackgroundImageId();
        }
        if (!sceneItemId) return;

        const values = Object.keys(newLayout).map((key) => ({
            key,
            value: newLayout[key],
            storePath: 'Data["0"].Layout["0"].$',
            cloneValue: true
        }));
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId,
            values
        }));
    }

    /**
     * Clear selection
     */
    selectRootScene() {
        this.container.activeSceneItemIds.next([this.sceneId]);
        this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.sceneId}));
    }

    getBlockNameBySceneId(i_sceneId): string {
        if (!i_sceneId) return null;
        const sceneItem = this.players.find((_player) => _player.$?.id == i_sceneId);
        if (!sceneItem) return null;
        // const label = this.storeSelector.getBlockLabelById(sceneItem.$.player, false);
        const block: IBlockLabelProp = this.storeSelector.getBlockLabelById(sceneItem.$.player, false);
        return block.name;
    }

    /**
     * Check if selected scene item is nested scene or not
     * @param id
     * @returns
     */
    checkNestedSceneById(id) {
        if (!id) return '';
        const sceneItem = this.findItem(id);
        if (!sceneItem) return '';

        if (isSubScene(sceneItem)) {
            return sceneItem.$.src;
        } else {
            const sceneSrc = this.getSrcFromMimeSceneItem(sceneItem);
            return !sceneSrc || sceneSrc == '-1' ? '' : sceneSrc;
        }
    }

    /**
     * Check if selected scene item is resource or not
     * @param id
     * @returns
     */
    checkResourceItemById(id) {
        if (!id) return '';

        const player = this.players.find((_player) => _player.$.id == id);
        if (isResourceItem(player)) {
            return player.Data["0"].Resource["0"]?.$.resource;
        } else {
            return '';
        }
    }

    /**
     * Check if selected scene item is label or not
     * @param id
     * @returns
     */
    checkLabelById(id) {
        if (!id) return false;
        const player = this.players.find((_player) => _player.$.id == id);
        return isLabelItem(player);
    }

    updateLastModified(sceneId, modified) {
        // NoHistory variant: this timestamp touch also fires on save during drill teardown, so routing it through
        // the tracked actionUpdateSceneItem would leave a spurious "lastModified" undo step on every drill.
        this.storeSelector.dispatch(actionUpdateSceneItemNoHistory({
            sceneId: `${sceneId}`,
            sceneItemId: `${sceneId}`,
            values: [{
                key: 'lastModified',
                value: modified,
                storePath: '$'
            }],
            disableCRUD: true
        }));
    }

    private getSrcFromMimeSceneItem(sceneItem) {
        switch (this.handleMimeAnomalies(sceneItem)) {
            case "STOCKS": {
                return sceneItem.Data[0]?.Stocks?.[0]?.Player[0]?.$.src;
            }
            case "XML": {
                return sceneItem.Data[0]?.Xml?.[0]?.Player[0]?.$.src;
            }
            case "CATALOG": {
                return sceneItem.Data[0]?.Catalog?.[0]?.Player[0]?.$.src;
            }
            case "CUSTOM_RSS": {
                if (sceneItem.Data[0]?.Rss?.[0]?.CustomRss) {
                    return sceneItem.Data[0]?.Rss?.[0]?.CustomRss[0]?.Player[0]?.$.src;
                } else {
                    return sceneItem.Data[0]?.Rss?.[1]?.CustomRss[0]?.Player[0]?.$.src;
                }
            }
            default: {
                return sceneItem.Data?.[0]?.Json?.[0]?.Player?.[0]?.$?.src;
            }
        }
    }

    private handleMimeAnomalies(sceneItem): MimeAnomalyType {
        const blockLabelRecords = this.storeSelector.blockLabels;
        switch (sceneItem?.$?.player) {
            case blockLabelRecords.STOCKS.id: {
                return 'STOCKS';
            }
            case blockLabelRecords.XML.id: {
                return 'XML';
            }
            case blockLabelRecords.CATALOG.id: {
                return 'CATALOG';
            }
            case blockLabelRecords.CUSTOMRSS.id: {
                return 'CUSTOM_RSS';
            }
            default: {
                const playerId = this.storeSelector.isBlockIdJsonAliased(sceneItem);
                switch (playerId) {
                    // case blockLabelRecords.NETFEED_NEWS.id: {
                    //     return 'NETFEED_NEWS';
                    // }
                    // case blockLabelRecords.NETFEED_HOROSCOPE.id: {
                    //     return 'NETFEED_HOROSCOPE';
                    // }
                    default: {
                        return 'JSON';
                    }
                }
            }
        }
    }

    /**
     * Scheduler Block specific services
     * @param
     * @returns
     */
    jumpToSchedulers(schedule_id) {
        this.localStoreService.setStorageValConfig('scheduledEnabled', '1');
        this.storeSelector.dispatch(actionSelectedSchedule({
            scheduleSelection: {
                scheduler: {
                    scheduleId: schedule_id,
                    unique: Math.random()
                }
            }
        }));
        this.router.navigateByUrl('/scheduler/editor');

        // const selectedSceneItemId$ = this.storeSelector.listenPlayerDataOfSelectedSceneItemId();
        // const schedules$ = this.storeSelector.getSchedules();
        // combineLatest([selectedSceneItemId$, schedules$])
        //     .pipe(take(1),
        //         map(([i_scene, i_schedule]) => {
        //             const sched = i_schedule.filter((v) => compareIds(v.schedule_id, i_scene.sceneItem.Data[0].Schedule[0].$.id));
        //             return sched.length === 1 ? sched[0] : null;
        //         })
        //     ).subscribe((selectedScheduler) => {
        //         if (isNullOrUndefOrEmpty(selectedScheduler)) return;
        //         this.localStoreService.setStorageValConfig('scheduledEnabled', '1');
        //
        //     }
        // );
    }

    /**
     * Playlist Block specific services
     * @returns
     */
    /**
     * Opens the asset picker dialog. The dialog (and everything it drags in) is editor-only, so it
     * is fetched on first use instead of being part of the shell every page downloads.
     * busyService.run shows the global loader while that happens.
     */
    private openGetAssetDialog(data: IDialogGetAsset, onClosed: (result: IDialogGetAsset[] | IDialogGetAsset) => void): void {
        // The dialog now opens a chunk-download later rather than instantly, so a second click
        // during that window used to open a second copy of it. Cleared as soon as the chunk
        // settles (not when the dialog closes), so a later legitimate open is never blocked.
        if (this.m_assetDialogOpening) {
            return;
        }
        this.m_assetDialogOpening = true;
        this.busyService.run(() => import("../components/dialog-get-asset/dialog-get-asset.component")).then(({DialogGetAssetComponent}) => {
            const dialogRefVal = this.dialog.open(DialogGetAssetComponent, {
                width: '70%',
                panelClass: 'custom-dlg-container',
                data
            });
            this.cod(
                dialogRefVal.afterClosed()
                    .pipe(take(1))
                    .subscribe((result: IDialogGetAsset[] | IDialogGetAsset) => onClosed(result))
            );
        }).catch((e) => console.error('failed to load asset dialog', e))
            .finally(() => this.m_assetDialogOpening = false);
    }

    public openAssetDialog(mode, multiSelect, callback = null) {
        const data: IDialogGetAsset = {mode: mode, multiSelect: multiSelect};
        this.openGetAssetDialog(data, (dialogGetAssets: IDialogGetAsset[] | IDialogGetAsset) => {
            if (!dialogGetAssets) return;
            if (multiSelect && !Array.isArray(dialogGetAssets)) return;
            if (callback) {
                callback(dialogGetAssets);
            }
        });
    }

    public openPlaylistAssociateDialog(sceneItem: ISceneBlock, addingNew, tableName: 'pageGps' | 'Page' = null) {
        const data: IDialogGetAsset = {mode: "SCENE_RESOURCE:PLAYER", mimeType: SceneMimeTypes.Player, multiSelect: true};
        this.openGetAssetDialog(data, (dialogGetAssets: IDialogGetAsset[]) => {
            if (!dialogGetAssets || !Array.isArray(dialogGetAssets)) return;
            this.addPlaylistTableItem(sceneItem, dialogGetAssets);
        });
    }

    public openMusicListAssociateDialog(sceneItem: ISceneBlock, addingNew, tableName: 'pageGps' | 'Page' = null) {
        const data: IDialogGetAsset = {mode: "RESOURCE:SOUND", mimeType: SceneMimeTypes.Player, multiSelect: true};
        this.openGetAssetDialog(data, (dialogGetAssets: IDialogGetAsset[]) => {
            if (!dialogGetAssets || !Array.isArray(dialogGetAssets)) return;
            this.addMusicTableItem(sceneItem, dialogGetAssets);
        });
    }

    public showToastOverLength() {
        this.toastr.error(this.translocoService.translate('general.tooLong24', {}, this.translocoService.getActiveLang()));
    }

    private addPlaylistTableItem(sceneItem: ISceneBlock, dialogGetAssets: IDialogGetAsset[]) {
        let pager: IPage;
        const pages = (sceneItem?.Data?.[0]?.Playlist?.[0]?.Page && _.isArray(sceneItem.Data[0].Playlist[0].Page)) ? _.cloneDeep(sceneItem.Data[0].Playlist[0].Page) : [];
        let len = getTotalLengthPlaylist(pages);

        for (const dialogGetAsset of dialogGetAssets) {
            switch (dialogGetAsset.selectedType) {
                case "RESOURCE:SOUND": {
                    const resource_type = dialogGetAsset.sourceData.resource_type.toLowerCase();
                    const playerId = (["mp3"].indexOf(resource_type) > -1) ? "3100" : "3130";
                    // const duration = this.getDuration(dialogGetAsset.sourceData?.resource_total_time);
                    pager = {
                        $: {
                            page: `${dialogGetAsset.sourceData.resource_name}`,
                            type: "resource"
                        },
                        Player: [
                            {
                                "$": {
                                    player: playerId,
                                    id: _.uniqueId('__'),
                                    label: sanitizeResourceLabel(dialogGetAsset.sourceData.resource_name)
                                },
                                Data: [
                                    {
                                        Resource: [
                                            {
                                                $: {
                                                    resource: dialogGetAsset.selected
                                                }
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    };
                    if (playerId === '3100') {
                        pager.$.duration = this.getDuration(sceneItem, dialogGetAsset.sourceData?.resource_total_time);
                    }
                    break;
                }

                case "RESOURCE_SELECTED": {
                    const resource_type = dialogGetAsset.sourceData.resource_type.toLowerCase();
                    const playerId = (["mp4", "mov", "m4v", "3gp"].indexOf(resource_type) > -1) ? "3100" : "3130";
                    // const duration = this.getDuration(dialogGetAsset.sourceData?.resource_total_time);
                    pager = {
                        $: {
                            page: `${dialogGetAsset.sourceData.resource_name}`,
                            type: "resource"
                        },
                        Player: [
                            {
                                "$": {
                                    player: playerId,
                                    id: _.uniqueId('__'),
                                    label: sanitizeResourceLabel(dialogGetAsset.sourceData.resource_name)
                                },
                                Data: [
                                    {
                                        Resource: [
                                            {
                                                $: {
                                                    resource: dialogGetAsset.selected
                                                }
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    };
                    if (playerId === '3100') {
                        pager.$.duration = this.getDuration(sceneItem, dialogGetAsset.sourceData?.resource_total_time);
                    }
                    break;
                }
                case "SCENE_SELECTED": {
                    pager = {
                        $: {
                            page: dialogGetAsset.sourceData.Player.$.label,
                            type: "scene",
                            // duration: "5"
                        },
                        Player: [
                            {
                                $: {
                                    src: dialogGetAsset.selected
                                }
                            }
                        ]
                    };
                    break;
                }
            }

            if (pager.$.duration) {
                len += parseFloat(pager.$.duration);
            }
            if (len > MAX_PLAYLIST_TIME) {
                this.showToastOverLength();
                return;
            }
            pages.push(pager);
        }
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId: sceneItem.$.id,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Playlist[0]'
            }]
        }));
        // super.initApplyStoreToDynamic(true);
    }

    private addMusicTableItem(sceneItem: ISceneBlock, dialogGetAssets: IDialogGetAsset[]) {
        let pager: IPage;
        const pages = sceneItem?.Data[0]?.Music[0].Page && _.isArray(sceneItem.Data[0].Music[0].Page) ? _.cloneDeep(sceneItem.Data[0].Music[0].Page) : [];
        let len = getTotalLengthPlaylist(pages);

        for (const dialogGetAsset of dialogGetAssets) {
            switch (dialogGetAsset.selectedType) {
                case "RESOURCE_SELECTED": {
                    // const resource_type = dialogGetAsset.sourceData.resource_type.toLowerCase();
                    // const playerId = (["mp3"].indexOf(resource_type) > -1) ? "4110" : "4110";
                    // const duration = this.getDuration(dialogGetAsset.sourceData?.resource_total_time);
                    const playerId = "3145";
                    pager = {
                        $: {
                            page: `${dialogGetAsset.sourceData.resource_name}`,
                            type: "resource"
                        },
                        Player: [
                            {
                                "$": {
                                    player: playerId,
                                    id: _.uniqueId('__')
                                },
                                Data: [
                                    {
                                        Resource: [
                                            {
                                                $: {
                                                    resource: dialogGetAsset.selected
                                                }
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    };
                    pager.$.duration = this.getDurationMusic(sceneItem, dialogGetAsset.sourceData?.resource_total_time);
                    break;
                }
                case "SCENE_SELECTED": {
                    pager = {
                        $: {
                            page: dialogGetAsset.sourceData.Player.$.label,
                            type: "scene",
                            // duration: "5"
                        },
                        Player: [
                            {
                                $: {
                                    src: dialogGetAsset.selected
                                }
                            }
                        ]
                    };
                    break;
                }
            }

            if (pager.$.duration) {
                len += parseFloat(pager.$.duration);
            }
            if (len > MAX_PLAYLIST_TIME) {
                this.showToastOverLength();
                return;
            }
            pages.push(pager);
        }
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId: sceneItem.$.id,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Music[0]'
            }]
        }));
        // super.initApplyStoreToDynamic(true);
    }

    private getDuration(sceneItem, currentDuration) {
        const defaultDuration = sceneItem.Data[0].Playlist[0].$.duration;
        if (currentDuration === '0' || isNullOrUndefOrEmpty(currentDuration)) {
            currentDuration = defaultDuration;
        }
        return String(Lib.ParseToFloatDouble(currentDuration));
    }

    private getDurationMusic(sceneItem, currentDuration) {
        const defaultDuration = sceneItem.Data[0].Music[0].$.duration;
        if (currentDuration === '0' || isNullOrUndefOrEmpty(currentDuration)) {
            currentDuration = defaultDuration;
        }
        return String(Lib.ParseToFloatDouble(currentDuration));
    }

    /**
     * Drill into a nested/referenced scene WITHOUT changing the browser URL. The URL keeps pointing at the
     * ROOT scene (the one opened from the list); the rendered scene follows the store's selected-scene-id,
     * which the scene editor's render stream is keyed on. This is what lets the browser back button move
     * between root scenes while the breadcrumb back-arrow moves within a single root's drill path.
     * Touch/slider drilling crosses into a different editor component, so those callers still route.
     */
    enterNestedScene(sceneId: Id) {
        if (!sceneId) return;
        this.storeSelector.dispatch(actionSelectedSceneId({ id: sceneId }));
    }

    /**
     * Whether a scene item is a CONTAINER, i.e. it holds content that the canvas cannot reach any other way:
     *  - a nested/referenced scene (sub-scene 3510, or a mime block carrying a scene src: JsonPlayer,
     *    JsonViewer, Stocks, Xml, Catalog, CustomRss) -> checkNestedSceneById
     *  - a Group (3520): an inner Scene of children
     *  - a content/data-provider block (Playlist, Collection, Swiper, Loader, Scheduler, Music-list,
     *    HourlyBalancer, Cart, ...) whose pages/items are dragged in from the toolbox. requiresToolboxContent
     *    already enumerates exactly this set, so it is reused rather than duplicated.
     * A LEAF component (Label, Clock, Image, ...) is NOT a container: it is edited by selecting it on the
     * canvas, so drilling into it would open a focused edit that shows nothing new.
     */
    private isContainerItem(item: ISceneBlock): boolean {
        if (!item) return false;
        if (this.checkNestedSceneById(item.$.id)) return true;
        if (isGroupItem(item)) return true;
        return requiresToolboxContent(this.storeSelector.getBlockLabelById(item.$.player, false));
    }

    /**
     * Whether a scene item can be drilled into (breadcrumb-bar drill-in button, canvas double-click, context
     * menu). Only CONTAINERS are drillable — see isContainerItem. Excludes the scene itself (multi-select falls
     * back to the scene id).
     * NOTE: a leaf component that lives INSIDE a container (e.g. a Label used as a playlist page) is still
     * drillable — that drill runs through the playlist timeline (playlist-editor/block-playlist jumpToPlayer),
     * not through here, and it is the only way to edit such a component.
     */
    isDrillableItem(itemId: Id): boolean {
        if (!itemId || itemId == this.sceneId) return false;
        return this.isContainerItem(this.findItem(itemId));
    }

    /**
     * A scene item by id, searched through the WHOLE scene tree — not just the top level. Groups nest (a group
     * inside a group), and while drilled into one the canvas/layers show its children, so the item being drilled
     * into, deleted, etc. is usually NOT a top-level player.
     */
    private findItem(itemId: Id): ISceneBlock {
        return findSceneItemDeep(this.players, itemId);
    }

    /**
     * Drill into the given scene item from the breadcrumb-bar drill-in button.
     * - A group / scene-embedding component -> enter that nested scene in place (same path as a canvas
     *   double-click, SceneBuilderComponent.linkToOtherScene): push the source into the trail
     *   ("scene > component > target scene"), then drill without changing the browser URL.
     * - A Group (player 3520, a container with an inner Scene of children) -> group-edit: the canvas shows the
     *   group's children as individually selectable/editable items (edits write back into the group by child id).
     * - A content/data-provider container (Playlist, Collection, Cart, ...) -> focused component-edit: the canvas
     *   renders ONLY this component (via a wrapper scene) and the toolbox stays open so its pages/items can be
     *   filled. Mirrors the playlist-editor drill-in path; the "scene > component" crumb is derived from the
     *   selection (componentMode).
     * Leaf components are NOT drillable (isContainerItem) — nothing sits below them.
     */
    drillIntoItem(itemId: Id) {
        if (!itemId) return;
        const item = this.findItem(itemId);
        if (!this.isContainerItem(item)) return;

        const src = this.checkNestedSceneById(itemId);
        if (src) {
            // Drilling DIRECTLY into a nested scene: the trail is "current scene > target scene". Do NOT push a
            // separate "via" crumb for the scene-reference item — a scene reference is labelled the same as the
            // scene it points to, so a via crumb would duplicate the target ("group > Campaign1 > Campaign1").
            // Instead stamp the ref's id on the parent crumb (parentItemId) so going back re-selects the ref in
            // the parent scene, matching the playlist/group drill's "back re-selects the parent" behaviour.
            //
            // If we are drilling out of a GROUP edit (the ref lives among a group's children), the group crumb is
            // only a DERIVED crumb (from the item path) — it is NOT in the store trail. Entering the nested scene
            // tears down group-edit, so that crumb would vanish, collapsing "scene > Group > target" to
            // "scene > target". Persist the group path into the store trail first so the middle crumb survives,
            // then leave group-edit so the nested scene opens as a clean, drillable full scene.
            const editTarget = this.container.componentEditTarget.value;
            const inGroupEdit = !!editTarget?.groupEdit;
            this.storeSelector.dispatch(actionSceneJumpLinked({ previousLinkedSceneId: this.sceneId }));
            this.storeSelector.getScene(this.sceneId).pipe(take(1)).subscribe((_scene) => {
                if (inGroupEdit) {
                    const baseTrail = this.breadcrumbs.length > 0
                        ? this.breadcrumbs.slice()
                        : [{ sceneId: this.sceneId, label: _scene?.Player?.$?.label || 'Scene' }];
                    const groupPath = getSceneItemPathCrumbs(_scene, editTarget.componentId, this.sceneId);
                    if (groupPath.length) {
                        this.storeSelector.dispatch(actionSceneBreadcrumbsSet({ breadcrumbs: [...baseTrail, ...groupPath] }));
                    }
                }
                // a nested scene opens as a full, standalone scene - always leave any focused component-edit we
                // drilled FROM (group OR a plain component/playlist/collection edit). A leftover componentEditTarget
                // keeps the editor in "focused component" state and HIDES the item layout panel on the nested scene.
                this.container.updateComponentEditTarget(null);
                this.container.updateEditorMode(null);
                this.handleSceneBreadcrumbs(
                    src,
                    this.sceneId,
                    _scene?.Player?.$?.label,
                    () => {
                        this.enterNestedScene(src);
                    },
                    undefined,
                    item.$.id,
                );
            });
            return;
        }

        // a Group is a container of children (in its own <Group> node) -> edit those children in place. groupEdit
        // builds a wrapper that lifts the children to top level (selectable moveables); the "scene > Group" crumb
        // stays. The toolbox stays OPEN: inside a group the left panel works like the scene's (layers list of the
        // group's children, drag/drop of components and resources INTO the group) - see SceneAddService.addTargetId.
        if (isGroupItem(item)) {
            this.container.updateComponentEditTarget({ sceneId: this.sceneId, componentId: itemId, groupEdit: true, needsToolbox: true });
            this.container.updateEditorMode('componentMode');
            this.storeSelector.dispatch(actionSelectedSceneItemId({ id: itemId }));
            return;
        }

        const needsToolbox = requiresToolboxContent(this.storeSelector.getBlockLabelById(item.$.player));
        // fitComponent -> the wrapper sizes the canvas to the component and moves it to the origin so it opens
        // BY ITSELF. No region is passed, so the focused-edit auto-zoom (scene-builder) never fires and the
        // user's current zoom is preserved.
        this.container.updateComponentEditTarget({ sceneId: this.sceneId, componentId: itemId, fitComponent: true, needsToolbox });
        this.container.updateEditorMode('componentMode');
        this.storeSelector.dispatch(actionSelectedSceneItemId({ id: itemId }));
    }

    /**
     * Handle breadcrumb navigation when linking to another scene
     * @param targetSceneId - The scene ID to navigate to
     * @param currentSceneId - The current scene ID (source)
     * @param currentSceneLabel - The current scene label (for breadcrumb)
     * @param callback - Callback function to execute after breadcrumb update
     */
    handleSceneBreadcrumbs(
        targetSceneId: Id,
        currentSceneId: Id,
        currentSceneLabel: string,
        callback?: (targetLabel?: string) => void,
        viaItem?: { id: Id, label: string },
        parentItemId?: Id
    ) {
        if (!targetSceneId) return;

        // Add current scene to breadcrumbs if this is the first jump. parentItemId (the item we drilled FROM,
        // e.g. a nested-scene reference) is stamped onto that crumb so clicking it / going back returns to the
        // parent scene with that item re-selected - matching how a playlist/group drill re-selects its parent.
        if (this.breadcrumbs.length === 0) {
            this.storeSelector.dispatch(actionSceneBreadcrumbsPush({
                breadcrumb: { sceneId: currentSceneId, label: currentSceneLabel || 'Scene', sceneItemId: parentItemId }
            }));
        }

        // jumping THROUGH an item (e.g. a scene-page inside a playlist) -> include that item crumb,
        // so the trail reads "scene > playlist > target scene"
        if (viaItem && viaItem.id) {
            this.storeSelector.dispatch(actionSceneBreadcrumbsPush({
                breadcrumb: { sceneId: currentSceneId, label: viaItem.label || 'item', sceneItemId: viaItem.id }
            }));
        }

        // Going forward - fetch target scene and add new breadcrumb
        this.storeSelector.getScene(targetSceneId).pipe(take(1)).subscribe((targetScene) => {
            const targetLabel = targetScene?.Player?.$.label || 'Scene';
            this.storeSelector.dispatch(actionSceneBreadcrumbsPush({
                breadcrumb: { sceneId: targetSceneId, label: targetLabel }
            }));
            if (callback) {
                callback(targetLabel);
            }
        });
    }

    /**
     * Push a breadcrumb that targets a component INSIDE the current scene (e.g. a component embedded in a
     * playlist page), as opposed to a separate nested scene. Starts the trail with the current scene if empty,
     * then adds the component crumb (same sceneId + the component's sceneItemId). Clicking it later re-selects
     * the component (see navigateToBreadcrumb), no scene navigation.
     */
    handleComponentBreadcrumb(sceneId: Id, sceneLabel: string, componentId: Id, componentLabel: string) {
        if (!componentId) return;
        if (this.breadcrumbs.length === 0) {
            this.storeSelector.dispatch(actionSceneBreadcrumbsPush({
                breadcrumb: { sceneId, label: sceneLabel || 'Scene' }
            }));
        }
        this.storeSelector.dispatch(actionSceneBreadcrumbsPush({
            breadcrumb: { sceneId, label: componentLabel || 'Component', sceneItemId: componentId }
        }));
    }

    /**
     * Navigate to the breadcrumb at the given index (clicked in the breadcrumb bar). Trims the trail to that
     * crumb, leaves any special editor mode (playlist/collection/...), then either re-selects an in-scene
     * component (crumb has sceneItemId) or navigates to the target scene (touch slider scenes use the touch
     * route). Lives here so the bar can be hosted by the whole scene editor, not a single sub-component.
     */
    navigateToBreadcrumb(index: number) {
        if (!this.breadcrumbs || index < 0 || index >= this.breadcrumbs.length) return;
        const target = this.breadcrumbs[index];
        if (!target || !target.sceneId) return;

        this.storeSelector.dispatch(actionSceneBreadcrumbsSet({ breadcrumbs: this.breadcrumbs.slice(0, index + 1) }));
        // clear any focused component-edit; a component crumb re-enters it below, a scene crumb exits it
        this.container.updateComponentEditTarget(null);
        this.container.updateEditorMode(null);

        // a crumb targeting an in-scene component: re-enter focused component edit (canvas shows only it)
        if (target.sceneItemId) {
            this.storeSelector.dispatch(actionSelectedSceneId({ id: target.sceneId }));
            this.storeSelector.dispatch(actionSelectedSceneItemId({ id: target.sceneItemId }));
            if (this.sceneId != target.sceneId) {
                this.router.navigateByUrl(`/editor/scene/${target.sceneId}`);
            }
            this.container.updateComponentEditTarget({ sceneId: target.sceneId, componentId: target.sceneItemId });
            this.container.updateEditorMode('componentMode');
            return;
        }

        // a scene crumb: navigate to that scene (main touch/slider scenes use the touch editor route)
        this.storeSelector.getScene(target.sceneId).pipe(take(1)).subscribe((targetScene) => {
            if (!isMainTouchScene(targetScene)) {
                this.storeSelector.dispatch(actionSelectedSceneId({ id: target.sceneId }));
                this.router.navigateByUrl(`/editor/scene/${target.sceneId}`);
            } else {
                const firstChildSceneId = TouchLib.getFirstSlideSceneId(targetScene);
                this.storeSelector.dispatch(actionSelectedSceneId({ id: firstChildSceneId }));
                this.storeSelector.dispatch(actionSelectedSceneTouchId(
                    TouchLib.createSelectedSliderPayload(targetScene.Player.$.id, targetScene.Player.$.label, firstChildSceneId)
                ));
                this.router.navigateByUrl(`/editor/touch/scene/${targetScene.Player.$.id}`);
            }
        });
    }

    /**
     * Put the user back exactly where an UNDO/REDO's edit was made, from the path RECORDED on the history step
     * (IDrillPath, stamped at dispatch time by StoreSelectorsService.dispatch), so the reverted change is visible
     * instead of silently reverting inside a container the user is no longer looking at.
     *
     * Restores BOTH dimensions of a drill position, because neither alone locates the edit:
     *  - the nested-scene crumb trail (store), and
     *  - the IN-scene target - group / focused component / component inside a playlist or collection page -
     *    which lives ONLY in ContainerService and whose breadcrumbs are DERIVED from it (getSceneItemPathCrumbs).
     *
     * The path is recorded rather than re-derived because it cannot be reconstructed after the fact: a group is a
     * <Group> block inside its parent scene, not its own scene record, so an edit made inside a group carries the
     * PARENT scene's sceneId - identical to an edit made at scene level. Same for a component embedded in a
     * playlist page. Only the dispatch-time UI state knows the difference.
     *
     * Returns `moved` - true if the user was actually taken somewhere (different scene OR different in-scene
     * target), telling the caller to select the reverted item rather than restore the prior selection - and
     * `sceneId`, the scene actually landed on, so the caller rebuilds THAT scene and cannot drift from us.
     */
    restoreDrillPath(drill: IDrillPath | null, fallbackSceneId: Id): { moved: boolean, sceneId: Id } {
        // step predates the drill stamp -> best-effort scene-level nav (nested-scene dimension only)
        if (!drill) {
            const moved = this.openSceneForHistoryNav(fallbackSceneId);
            return { moved, sceneId: moved ? fallbackSceneId : this.sceneId };
        }

        const targetSceneId = drill.sceneId ?? fallbackSceneId;
        if (targetSceneId == null) return { moved: false, sceneId: this.sceneId };

        // A later edit may have deleted the group/component we were drilled into (e.g. undoing an edit that is
        // older than the delete that removed its container). Re-entering it would open an empty wrapper, so
        // verify it still exists and drop back to scene level if not.
        let editTarget = drill.editTarget;
        if (editTarget?.componentId != null) {
            const recorded = editTarget;
            let scenes: IPlayerProps[] = [];
            this.storeSelector.getScenes(false, true).pipe(take(1)).subscribe((s) => (scenes = s || []));
            const host = scenes.find((s) => `${s?.Player?.$?.id}` === `${recorded.sceneId ?? targetSceneId}`);
            const items = host?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player || [];
            if (!findSceneItemDeep(items, recorded.componentId)) editTarget = null;
        }

        const sameScene = `${targetSceneId}` === `${this.sceneId}`;
        const sameTarget = `${this.container.componentEditTarget.value?.componentId ?? ''}` === `${editTarget?.componentId ?? ''}`;

        // Order matters: the in-scene level's crumbs are derived from componentEditTarget against the CURRENT
        // scene, so the trail and the scene must be in place before the target is re-entered. Only write the trail
        // when it actually differs - undo is a hot path and the reducer hands out a new array reference every
        // time, waking every crumb subscriber for nothing.
        const crumbs = (drill.crumbs || []).map((c) => ({...c}));
        if (JSON.stringify(this.breadcrumbs || []) !== JSON.stringify(crumbs)) {
            this.storeSelector.dispatch(actionSceneBreadcrumbsSet({ breadcrumbs: crumbs }));
        }
        if (!sameScene) this.storeSelector.dispatch(actionSelectedSceneId({ id: targetSceneId }));

        // componentMode is meaningless without its target, so a dropped target drops the mode with it. Any other
        // recorded mode (playlist/collection/location) is independent of componentEditTarget - restore it as-is.
        const mode = editTarget
            ? (drill.editorMode ?? 'componentMode')
            : (drill.editorMode === 'componentMode' ? null : (drill.editorMode ?? null));
        this.container.updateComponentEditTarget(editTarget);
        this.container.updateEditorMode(mode);

        return { moved: !sameScene || !sameTarget, sceneId: targetSceneId };
    }

    /**
     * Fallback for history steps with no recorded drill path: open the scene that an UNDO/REDO just reverted so
     * the user at least sees the change. The undo history is one global stack across ALL scene records (parent +
     * nested), so an undo can revert an edit in a scene that is NOT the one currently on screen (e.g. you edited a
     * nested scene, drilled out, then pressed undo). Given the target scene id (resolved from the history patch),
     * re-open it in place: rebuild the drill trail from the current root down to it and select it - no URL change,
     * mirroring enterNestedScene. Returns true if it actually navigated to a different scene (false when the
     * target is already on screen or unknown).
     *
     * Recovers only the nested-scene dimension - the DFS cannot see group/playlist-page drilling. Prefer
     * restoreDrillPath, which replays the recorded position.
     */
    openSceneForHistoryNav(targetSceneId: Id): boolean {
        if (targetSceneId == null || `${targetSceneId}` === `${this.sceneId}`) return false;

        let scenes: IPlayerProps[] = [];
        this.storeSelector.getScenes(false, true).pipe(take(1)).subscribe((s) => (scenes = s || []));

        // root of the current drill tree: the first crumb, or the current (top-level) scene when not drilled
        const rootId = (this.breadcrumbs && this.breadcrumbs.length) ? this.breadcrumbs[0].sceneId : this.sceneId;
        const trail = this.buildTrailToScene(scenes, rootId, targetSceneId);

        // leave any focused component/group edit so the target opens as a normal, drillable scene
        this.container.updateComponentEditTarget(null);
        this.container.updateEditorMode(null);

        if (trail && trail.length > 1) {
            this.storeSelector.dispatch(actionSceneBreadcrumbsSet({ breadcrumbs: trail }));
        } else if (`${targetSceneId}` === `${rootId}`) {
            // target IS the root - clear the drill trail so the bar returns to the root scene
            this.storeSelector.dispatch(actionSceneBreadcrumbsSet({ breadcrumbs: [] }));
        } else {
            // not reachable from the current root (rare) - still open it with a minimal trail so it is at least
            // labelled and navigable
            const scene = scenes.find((s) => `${s?.Player?.$?.id}` === `${targetSceneId}`);
            this.storeSelector.dispatch(actionSceneBreadcrumbsSet({
                breadcrumbs: [{ sceneId: targetSceneId, label: scene?.Player?.$?.label || 'Scene' }]
            }));
        }

        this.storeSelector.dispatch(actionSelectedSceneId({ id: targetSceneId }));
        return true;
    }

    /**
     * Best-effort drill trail from rootSceneId down to targetSceneId, following nested-scene references
     * (sub-scene 3510 / mime-with-src) through the scene-reference graph. Returns breadcrumb entries
     * [rootCrumb, ..., targetCrumb] or null if the target is not reachable from the root. Intermediate crumbs are
     * left as plain scene crumbs (no sceneItemId) so clicking one just navigates to that scene.
     */
    private buildTrailToScene(scenes: IPlayerProps[], rootSceneId: Id, targetSceneId: Id): Array<any> | null {
        const byId = new Map<string, IPlayerProps>((scenes || []).map((s) => [`${s?.Player?.$?.id}`, s]));
        const labelOf = (id) => byId.get(`${id}`)?.Player?.$?.label || 'Scene';
        const visited = new Set<string>();

        const dfs = (sceneId): Array<any> | null => {
            if (`${sceneId}` === `${targetSceneId}`) {
                return [{ sceneId: targetSceneId, label: labelOf(targetSceneId) }];
            }
            if (visited.has(`${sceneId}`)) return null;
            visited.add(`${sceneId}`);
            const scene = byId.get(`${sceneId}`);
            if (!scene) return null;
            for (const { childSceneId } of this.getSceneChildRefs(scene)) {
                const sub = dfs(childSceneId);
                if (sub) return [{ sceneId, label: labelOf(sceneId) }, ...sub];
            }
            return null;
        };
        return dfs(rootSceneId);
    }

    /**
     * All nested-scene references inside one scene record: every item (top-level, group child, or playlist/
     * collection page) that points at another scene, as { childSceneId, item }. Mirrors checkNestedSceneById's
     * "sub-scene src OR mime scene src" rule but operates on an arbitrary scene rather than the current one.
     */
    private getSceneChildRefs(scene: IPlayerProps): Array<{ childSceneId: Id, item: any }> {
        const refs: Array<{ childSceneId: Id, item: any }> = [];
        const rootItems = scene?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player || [];

        const walk = (items) => {
            for (const it of items || []) {
                let ref: any = '';
                if (isSubScene(it)) {
                    ref = it?.$?.src || '';
                } else {
                    const s = this.getSrcFromMimeSceneItem(it);
                    ref = (!s || s === '-1') ? '' : s;
                }
                if (ref) refs.push({ childSceneId: ref, item: it });

                const nested = [
                    ...(getGroupChildren(it) || []),
                    ...[...(it?.Data?.[0]?.Playlist?.[0]?.Page || []), ...(it?.Data?.[0]?.Collection?.[0]?.Page || [])]
                        .map((pg) => pg?.Player?.[0])
                        .filter(Boolean),
                ];
                if (nested.length) walk(nested);
            }
        };
        walk(rootItems);
        return refs;
    }

    /**
     * Click handler for the breadcrumb bar. The crumb is either a scene crumb (no sceneItemId) - part of the
     * nested-scene trail when trailIndex >= 0, else the fallback current-scene crumb - or an item crumb
     * (sceneItemId) within the current scene. A component crumb (carries its playlist region) re-enters the
     * focused component-edit mode; a plain item crumb just selects it; a scene crumb navigates / selects the scene.
     */
    goToBreadcrumb(crumb: any, trailIndex: number) {
        if (!crumb || !crumb.sceneId) return;
        // always leave focused component edit first
        this.container.updateComponentEditTarget(null);
        this.container.updateEditorMode(null);

        // a crumb that lives in the manual trail (scene OR item) -> trim the trail to it
        if (trailIndex >= 0) {
            this.storeSelector.dispatch(actionSceneBreadcrumbsSet({ breadcrumbs: this.breadcrumbs.slice(0, trailIndex + 1) }));
        }

        // an item/component crumb -> select it; navigate to its scene if we're not already there
        if (crumb.sceneItemId) {
            const crossScene = this.sceneId != crumb.sceneId;
            // in-place scene switch (no URL change): the render stream follows the selected-scene-id
            this.storeSelector.dispatch(actionSelectedSceneId({ id: crumb.sceneId }));
            this.storeSelector.dispatch(actionSelectedSceneItemId({ id: crumb.sceneItemId }));
            // Resolve the crumb's item in ITS OWN scene. Going BACK from a nested scene to a Group crumb is
            // cross-scene (current scene is the nested one, the group lives in the crumb's scene), so findItem —
            // which only searches the current scene's players — cannot see it. Search the crumb's scene directly
            // so a Group crumb still re-enters group-edit instead of collapsing to a plain single-item select.
            let crumbItem = this.findItem(crumb.sceneItemId);
            if (!crumbItem && crossScene) {
                this.storeSelector.getScene(crumb.sceneId).pipe(take(1)).subscribe((targetScene) => {
                    const players = targetScene?.Player?.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player || [];
                    crumbItem = findSceneItemDeep(players, crumb.sceneItemId);
                });
            }
            if (isGroupItem(crumbItem)) {
                // a GROUP crumb -> re-enter group edit on it. This is how you come back OUT of a group nested in
                // a group ("scene > Group > Group2", click "Group"): without this the group would open as a
                // focused single-component edit instead of showing its children.
                this.container.updateComponentEditTarget({ sceneId: crumb.sceneId, componentId: crumb.sceneItemId, groupEdit: true, needsToolbox: true });
                this.container.updateEditorMode('componentMode');
                return;
            }
            if (crumb.regionWidth && crumb.regionHeight) {
                this.container.updateComponentEditTarget({ sceneId: crumb.sceneId, componentId: crumb.sceneItemId, regionWidth: crumb.regionWidth, regionHeight: crumb.regionHeight });
                this.container.updateEditorMode('componentMode');
            } else if (crossScene) {
                // the target scene's canvas isn't rendered yet -> one-shot request that scene-builder
                // applies once that scene's moveables exist (handles a 'created' OR 'updated' load).
                this.container.requestCanvasSelect.next({ sceneId: crumb.sceneId, ids: [crumb.sceneItemId] });
            } else if (this.isContainerItem(crumbItem) && !this.checkNestedSceneById(crumb.sceneItemId)) {
                // a same-scene item crumb that points at a drillable CONTAINER (e.g. the "Playlist" crumb in
                // "group > Group > Playlist > Group"). When the trail is fully DERIVED from the drilled-in
                // component path (getSceneItemPathCrumbs, store trail empty) it only exists while componentMode
                // is on. Plain-selecting the item would exit componentMode (updateEditorMode(null) above) and
                // collapse the ENTIRE derived trail back to the root scene crumb — so back/click on a middle
                // crumb jumped straight to the root instead of stepping one level. Re-drill into the container
                // so it re-enters that container's focused (playlist/collection/...) edit and the breadcrumb
                // steps back exactly one level. Groups are already handled above; nested-scene refs are excluded
                // (they change scene and belong to the scene-trail path, not an in-place re-drill).
                this.drillIntoItem(crumb.sceneItemId);
            } else {
                // same scene -> the moveables already exist, select on the canvas immediately
                this.container.activeSceneItemIds.next([crumb.sceneItemId]);
            }
            return;
        }

        // a scene crumb in the trail -> navigate to that scene (touch slider scenes use the touch route)
        if (trailIndex >= 0) {
            this.storeSelector.getScene(crumb.sceneId).pipe(take(1)).subscribe((targetScene) => {
                if (!isMainTouchScene(targetScene)) {
                    // in-place scene switch (no URL change): the render stream follows the selected-scene-id
                    this.storeSelector.dispatch(actionSelectedSceneId({ id: crumb.sceneId }));
                    this.storeSelector.dispatch(actionSelectedSceneItemId({ id: crumb.sceneId }));
                    // select the whole scene (deselect any item) in the layers + canvas, which read
                    // activeSceneItemIds; without this a previously-selected item stays highlighted
                    this.container.activeSceneItemIds.next([crumb.sceneId]);
                } else {
                    const firstChildSceneId = TouchLib.getFirstSlideSceneId(targetScene);
                    this.storeSelector.dispatch(actionSelectedSceneId({ id: firstChildSceneId }));
                    this.storeSelector.dispatch(actionSelectedSceneTouchId(
                        TouchLib.createSelectedSliderPayload(targetScene.Player.$.id, targetScene.Player.$.label, firstChildSceneId)
                    ));
                    this.router.navigateByUrl(`/editor/touch/scene/${targetScene.Player.$.id}`);
                }
            });
            return;
        }

        // the fallback current-scene crumb -> select the scene itself (deselect any item)
        this.storeSelector.dispatch(actionSelectedSceneId({ id: crumb.sceneId }));
        this.storeSelector.dispatch(actionSelectedSceneItemId({ id: crumb.sceneId }));
        this.container.activeSceneItemIds.next([crumb.sceneId]);
    }

    /**
     * Open rich text editor dialog for a specific scene item
     */
    public openRichTextEditor(sceneItemId: string) {
        // Find the scene item
        const sceneItem = this.players.find(player => player.$?.id === sceneItemId);
        if (!sceneItem) {
            console.warn('RichText block not found:', sceneItemId);
            return;
        }

        // A migrated LEGACY RichText (player 3240) stores its content as a Flash <Text> blob rather than
        // <RichText htmlText>. We edit it in the same editor by converting Flash -> htmlText on open and
        // htmlText -> Flash on save, keeping the node in the legacy shape both players render.
        const data: any = sceneItem?.Data?.[0];
        const isLegacy = sceneItem?.$?.player === '3240' || (!data?.RichText && !!data?.Text);

        // Get current content and font style
        const currentContent = isLegacy
            ? flashTextToHtml(extractLegacyText(data?.Text))
            : (data?.RichText?.[0]?.$?.htmlText || '');

        // Get background color from Data[0].Background[0].$.color
        const backgroundColor = (sceneItem as any)?.Data?.[0]?.Background?.[0]?.$?.color;

        // Open dialog
        const dialogData: RichTextEditorDialogData = {
            content: currentContent,
            backgroundColor: backgroundColor ? Lib.DecimalToHex(backgroundColor) : undefined
        };

        // Handle save
        const onSave = (result) => {
                if (result?.content !== undefined) {
                    if (isLegacy) {
                        // Convert the edited htmlText back to the legacy Flash <Text> blob and rewrite the whole
                        // Text node (preserving its attributes), keeping player id 3240 so legacy still renders it.
                        const flash = htmlToFlashText(result.content);
                        this.storeSelector.dispatch(actionUpdateSceneItem({
                            sceneId: this.sceneId,
                            sceneItemId: sceneItemId,
                            values: [{
                                key: 'Text',
                                value: [{$: legacyTextAttrs(data?.Text), _: flash}],
                                storePath: 'Data[0]'
                            }]
                        }));
                    } else {
                        // Save the content to the store
                        this.storeSelector.dispatch(actionUpdateSceneItem({
                            sceneId: this.sceneId,
                            sceneItemId: sceneItemId,
                            values: [{
                                key: 'htmlText',
                                value: result.content,
                                storePath: 'Data[0].RichText[0].$'
                            }]
                        }));
                    }
                }
        };

        // lazy: the rich text editor (and the html2canvas / kolkov editor it drags in) is fetched
        // on first use, with the global loader shown while it downloads
        if (this.m_richTextDialogOpening) {
            return;
        }
        this.m_richTextDialogOpening = true;
        this.busyService.run(() => import("../components/dialog-rich-text-editor/dialog-rich-text-editor.component")).then(({DialogRichTextEditorComponent}) => {
            const dialogRef = this.dialog.open(DialogRichTextEditorComponent, {
                width: '1000px',
                maxHeight: '90vh',
                disableClose: false,
                data: dialogData
            });
            this.cod(
                dialogRef.afterClosed().pipe(take(1)).subscribe(onSave)
            );
        }).catch((e) => console.error('failed to load rich text editor dialog', e))
            .finally(() => this.m_richTextDialogOpening = false);
    }
}
